import {
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';
import { useMemo } from 'react';
import { AdvancedSettingsAPI } from 'api/advanced-settings/advanced-settings';
import {
  AdvancedSetting,
  SettingType,
} from 'api/advanced-settings/settings-types';

const baseOptions = {
  refetchOnWindowFocus: false,
  refetchOnMount: false,
};

export const getQueryAdvancedSettingsConfigKey = () => [
  'AdvancedSettingsConfig',
];
export const useQueryAdvancedSettingsConfig = (
  options?: UseQueryOptions<
    AdvancedSetting[],
    unknown,
    AdvancedSetting[],
    string[]
  >,
): UseQueryResult<AdvancedSetting[], unknown> =>
  useQuery(
    getQueryAdvancedSettingsConfigKey(),
    () => AdvancedSettingsAPI.getAdvancedSettingsConfig(),
    { ...baseOptions, ...options },
  );

export const useMutateAdvancedSettingsConfig = () => {
  const queryClient = useQueryClient();

  return useMutation(
    (data: AdvancedSetting) =>
      AdvancedSettingsAPI.updateAdvancedSettingsConfig(data),
    {
      onMutate: async (updatedSetting: AdvancedSetting) => {
        const previousSettings: AdvancedSetting[] =
          queryClient.getQueryData(getQueryAdvancedSettingsConfigKey()) ?? [];

        const filteredSettings = previousSettings.filter(
          (setting) => setting.eventType !== updatedSetting.eventType,
        );

        await queryClient.setQueryData(getQueryAdvancedSettingsConfigKey(), [
          ...filteredSettings,
          updatedSetting,
        ]);

        return previousSettings;
      },
      onError: async (err, _, context) => {
        await queryClient.setQueryData(
          getQueryAdvancedSettingsConfigKey(),
          context,
        );
      },
    },
  );
};

export const getAlignPhaseDatesSetting = (
  settings: AdvancedSetting[] | undefined,
): boolean => {
  return (
    settings?.find(({ eventType }) => eventType === SettingType.AlignPhaseDates)
      ?.eventValue || false
  );
};

export const getNewDependenciesEnabledSetting = (
  settings: AdvancedSetting[] | undefined,
): boolean => {
  return (
    settings?.find(
      ({ eventType }) => eventType === SettingType.EnableNewDependenciesAndLag,
    )?.eventValue || false
  );
};

export const useGetAlignPhaseDatesSetting = () => {
  const { data: advancedSettingsConfig = [] } =
    useQueryAdvancedSettingsConfig();

  return useMemo(
    () => getAlignPhaseDatesSetting(advancedSettingsConfig),
    [advancedSettingsConfig],
  );
};

export const useGetNewDependenciesEnabledSetting = () => {
  const { data: advancedSettingsConfig = [] } =
    useQueryAdvancedSettingsConfig();

  return useMemo(
    () =>
      advancedSettingsConfig.find(
        ({ eventType }) =>
          eventType === SettingType.EnableNewDependenciesAndLag,
      )?.eventValue || false,
    [advancedSettingsConfig],
  );
};
